import { useEffect, useState } from "react";
import { isValid, parseISO } from "date-fns";
import { useTranslation } from "react-i18next";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";

import { MAX_COST_INDEX, MEDIAN_COST_INDEX, MIN_COST_INDEX } from "./constants";
import PriceAdvisorRepairLaborLink from "./PriceAdvisorRepairLaborLink";
import PriceLine from "./PriceLine";
import PriceTitle from "./PriceTitle";
import RoomForImprovementBanner from "./RoomForImprovementBanner";
import {
  LaborRecommendation,
  RepairRecommendation,
  ServiceCenter,
} from "./types";
import {
  buildRepairLaborFilter,
  getVendorLocation,
  getVendorName,
} from "./utils";

interface Props {
  laborRecommendation: LaborRecommendation;
  recommendation: RepairRecommendation;
  serviceCentersByID: Record<string, ServiceCenter>;
  serviceCenterCurrent: string;
  assetFilters: string;
  workOrderID: string;
  workOrderDate: string;
  consideredHistoryDays: number;
  isStreaming: boolean;
  hasAssetData: boolean;
}

const LaborRecommendationSection = ({
  laborRecommendation,
  recommendation,
  serviceCentersByID,
  serviceCenterCurrent,
  assetFilters,
  workOrderID,
  workOrderDate,
  consideredHistoryDays,
  isStreaming,
  hasAssetData,
}: Props) => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(
    laborRecommendation.room_for_improvement
  );

  useEffect(() => {
    setIsExpanded(laborRecommendation.room_for_improvement);
  }, [laborRecommendation.room_for_improvement]);

  if (!laborRecommendation.service_center_alternatives) {
    return null;
  }

  const hasExpandableContent =
    laborRecommendation.service_center_alternatives.length > 0;

  const minimum = Math.min(
    ...laborRecommendation.service_center_alternatives.map(
      (alternative) =>
        alternative.cost_min_median_max?.[MIN_COST_INDEX] ?? Infinity
    ),
    laborRecommendation.cost_labor ?? Infinity
  );

  const maximum = Math.max(
    ...laborRecommendation.service_center_alternatives.map(
      (alternative) =>
        alternative.cost_min_median_max?.[MAX_COST_INDEX] ?? -Infinity
    ),
    laborRecommendation.cost_labor ?? -Infinity,
    0
  );

  const parsedWorkOrderDate = workOrderDate
    ? parseISO(workOrderDate)
    : undefined;
  const canBuildLinks =
    hasAssetData &&
    Boolean(workOrderDate) &&
    Boolean(workOrderID) &&
    Number.isFinite(consideredHistoryDays) &&
    Boolean(parsedWorkOrderDate) &&
    isValid(parsedWorkOrderDate);

  return (
    <>
      <TableRow>
        <TableCell>
          <div className="flex space-x-2">
            <span className="font-semibold ">
              {laborRecommendation.labor_description}
            </span>
            <span className="text-sm">
              {laborRecommendation.vmrs_id || laborRecommendation.labor_id}
            </span>
            <span className="text-sm">
              {laborRecommendation.labor_quantity &&
                laborRecommendation.labor_quantity > 1 &&
                `(${laborRecommendation.labor_quantity}x)`}
            </span>
          </div>
        </TableCell>
        <TableCell>
          <PriceTitle
            minimum={minimum}
            maximum={maximum}
            quoted={laborRecommendation.cost_labor}
            currencyCode={recommendation.currency_code}
            aiEstimate={laborRecommendation.labor_hours_estimate}
            hasExpandableContent={hasExpandableContent}
            onToggleExpand={() => setIsExpanded(!isExpanded)}
            isExpanded={isExpanded}
          />
        </TableCell>
      </TableRow>
      {laborRecommendation.room_for_improvement && (
        <TableRow
          sx={{
            display: isExpanded ? "table-row" : "none",
            opacity: isExpanded ? 1 : 0,
            transition: "opacity 300ms",
          }}
        >
          <TableCell colSpan={2} sx={{ p: 0, borderBottom: "none" }}>
            <RoomForImprovementBanner />
          </TableCell>
        </TableRow>
      )}
      {laborRecommendation.service_center_alternatives.map(
        (alternative, index) => (
          <TableRow
            key={`${alternative.service_center_id || "aggregated"}-${index}`}
            sx={{
              display: isExpanded ? "table-row" : "none",
              opacity: isExpanded ? 1 : 0,
              transition: "opacity 300ms",
            }}
          >
            <TableCell>
              <div className="ml-4">
                <div className="flex space-x-2 items-center">
                  <span className="font-semibold text-xs">
                    {getVendorName(
                      serviceCentersByID,
                      t,
                      alternative.service_center_id,
                      isStreaming
                    )}
                  </span>
                  {canBuildLinks && (
                    <PriceAdvisorRepairLaborLink
                      repairLaborFilter={buildRepairLaborFilter(
                        laborRecommendation.vmrs_id,
                        recommendation.repair_id,
                        laborRecommendation.labor_quantity,
                        workOrderDate,
                        workOrderID,
                        consideredHistoryDays,
                        laborRecommendation.min_threshold_cost_labor,
                        laborRecommendation.max_threshold_cost_labor,
                        alternative.service_center_id
                          ? serviceCentersByID[alternative.service_center_id]
                              ?.brand_name
                          : undefined
                      )}
                      repairLaborCount={alternative.repairs_count}
                      assetFilter={assetFilters}
                    />
                  )}
                </div>
                <div className="text-xs text-neutral-500">
                  {getVendorLocation(
                    serviceCentersByID,
                    serviceCenterCurrent,
                    t,
                    alternative.service_center_id
                  )}
                </div>
              </div>
            </TableCell>
            <TableCell>
              <PriceLine
                quoted={laborRecommendation.cost_labor}
                minimum={minimum}
                maximum={maximum}
                current={alternative.cost_min_median_max?.[MEDIAN_COST_INDEX]!}
                currentMin={alternative.cost_min_median_max?.[MIN_COST_INDEX]}
                currentMax={alternative.cost_min_median_max?.[MAX_COST_INDEX]}
                currencyCode={recommendation.currency_code}
              />
            </TableCell>
          </TableRow>
        )
      )}
    </>
  );
};

export default LaborRecommendationSection;
